/**
 * The idle session, matching the Tab Service portal.
 *
 * Five minutes without a mouse, a key or a touch and the warning dialog opens with a minute
 * on the clock. Continue extends the session; the clock running out, or Sign out, ends it.
 * While somebody is working the session is extended quietly every four minutes, so the
 * warning only ever appears to somebody who has actually stopped.
 *
 * The numbers are the portal's, so a person moving between the two products meets the same
 * behaviour on both. Every open tab hears about the answer, so the dialog does not linger in
 * one tab after another tab has already continued or signed out.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { authStore } from './auth-store';

/** Quiet time before the warning opens. The portal's idle timer uses the same five minutes. */
export const IDLE_BEFORE_WARNING_MS = 5 * 60 * 1000;
/** How long the warning counts down before the session ends. */
export const WARNING_SECONDS = 60;
/** How often an active session is extended in the background. */
export const EXTEND_INTERVAL_MS = 4 * 60 * 1000;

const CHANNEL = 'tabfile-session';
const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'wheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'visibilitychange',
] as const;

type SessionMessage = { type: 'extended' } | { type: 'ended' };

export interface SessionTimeout {
  /** True while the warning dialog should be showing. */
  warning: boolean;
  /** Seconds left before the session ends, counting down from WARNING_SECONDS. */
  secondsLeft: number;
  /** True while Continue is in flight. */
  extending: boolean;
  /** Answer the warning with Continue. */
  extend: () => void;
}

/**
 * @param active whether there is a session to watch at all.
 * @param onEnded called once when the session has ended, by the clock or by Sign out.
 */
export function useSessionTimeout(active: boolean, onEnded: () => void): SessionTimeout {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);
  const [extending, setExtending] = useState(false);

  const lastActivity = useRef(Date.now());
  const lastExtend = useRef(Date.now());
  const channel = useRef<BroadcastChannel | null>(null);
  const endedRef = useRef(false);
  // Held in a ref so the watcher below is set up once and never torn down by a new callback.
  const onEndedRef = useRef(onEnded);
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  const end = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    setWarning(false);
    onEndedRef.current();
  }, []);

  const extend = useCallback(() => {
    setExtending(true);
    void authStore
      .refreshSession()
      .then(() => {
        lastActivity.current = Date.now();
        lastExtend.current = Date.now();
        setWarning(false);
        setSecondsLeft(WARNING_SECONDS);
        channel.current?.postMessage({ type: 'extended' } satisfies SessionMessage);
      })
      // A session that cannot be extended is already over; ending it says so plainly.
      .catch(() => end())
      .finally(() => setExtending(false));
  }, [end]);

  // Hear what the other tabs did.
  useEffect(() => {
    if (!active || typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel(CHANNEL);
    channel.current = bc;
    bc.onmessage = (event: MessageEvent<SessionMessage>) => {
      if (event.data?.type === 'extended') {
        lastActivity.current = Date.now();
        lastExtend.current = Date.now();
        setWarning(false);
        setSecondsLeft(WARNING_SECONDS);
      } else if (event.data?.type === 'ended') {
        end();
      }
    };
    return () => {
      channel.current = null;
      bc.close();
    };
  }, [active, end]);

  // Watch for quiet, and keep an active session alive.
  useEffect(() => {
    if (!active) {
      endedRef.current = false;
      setWarning(false);
      setSecondsLeft(WARNING_SECONDS);
      return;
    }
    lastActivity.current = Date.now();
    lastExtend.current = Date.now();

    const onActivity = () => {
      lastActivity.current = Date.now();
    };
    for (const event of ACTIVITY_EVENTS) window.addEventListener(event, onActivity, { passive: true });

    const tick = window.setInterval(() => {
      const now = Date.now();
      const idle = now - lastActivity.current;
      setWarning((open) => open || idle >= IDLE_BEFORE_WARNING_MS);
      // Somebody still working keeps their session without ever seeing the dialog.
      if (idle < IDLE_BEFORE_WARNING_MS && now - lastExtend.current >= EXTEND_INTERVAL_MS) {
        lastExtend.current = now;
        void authStore.refreshSession().catch(() => undefined);
      }
    }, 5000);

    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, onActivity);
      window.clearInterval(tick);
    };
  }, [active]);

  // The minute on the clock.
  useEffect(() => {
    if (!warning) return;
    setSecondsLeft(WARNING_SECONDS);
    const countdown = window.setInterval(() => {
      setSecondsLeft((left) => {
        if (left <= 1) {
          window.clearInterval(countdown);
          channel.current?.postMessage({ type: 'ended' } satisfies SessionMessage);
          end();
          return 0;
        }
        return left - 1;
      });
    }, 1000);
    return () => window.clearInterval(countdown);
  }, [warning, end]);

  return { warning, secondsLeft, extending, extend };
}

/** Tell the other tabs the session is over, so their dialogs close with it. */
export function announceSessionEnded(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const bc = new BroadcastChannel(CHANNEL);
  bc.postMessage({ type: 'ended' } satisfies SessionMessage);
  bc.close();
}

/** `1:00`, the way the portal writes the same clock. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}
